import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const BLOCK_TITLES = {
  purpose: "Purpose",
  instructions: "Brief instructions",
  current: "Current configuration",
  dependencies: "Dependencies (where used)",
  status: "Status",
  actions: "Actions",
  preview: "Preview / test",
  audit: "Audit history",
  help: "Help",
};

export default function AdminCenter() {
  const [areas, setAreas] = useState([]);
  const [health, setHealth] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [exceptions, setExceptions] = useState(null);
  const [draft, setDraft] = useState({});
  const [certificate, setCertificate] = useState(null);
  const [notice, setNotice] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [area, setArea] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadExceptions = useCallback(() => {
    apiFetch("/acceptance/exceptions").then((r) => (r.ok ? r.json() : null)).then(setExceptions);
    apiFetch("/acceptance/certificate").then((r) => (r.ok ? r.json() : null)).then(setCertificate);
  }, []);

  const loadCleanup = useCallback(() => {
    apiFetch("/data-cleanup/preview")
      .then((r) => (r.ok ? r.json() : null))
      .then(setCleanup);
  }, []);

  const runCleanup = async () => {
    setCleanupBusy(true);
    const r = await apiFetch("/data-cleanup/run", {
      method: "POST", body: JSON.stringify({ confirmation }),
    });
    const out = await r.json().catch(() => ({}));
    setCleanupBusy(false);
    setCleanupResult(out.message || "The cleanup could not be run.");
    if (out.ok) { setConfirmation(""); loadCleanup(); }
  };

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    apiFetch("/admin-center/areas").then(async (r) => {
      if (r.status === 403) { setError("You do not have administration access (config.read)."); return; }
      if (!r.ok) { setError("Unable to load the Administration Center."); return; }
      setAreas(await r.json());
    });
    apiFetch("/admin-center/health").then((r) => (r.ok ? r.json() : null)).then(setHealth);
    apiFetch("/platform/health").then((r) => (r.ok ? r.json() : null)).then(setPlatform);
    loadExceptions();
    loadCleanup();
  }, [navigate, loadExceptions, loadCleanup]);

  const act = async (path, body, fallback) => {
    const r = await apiFetch(path, { method: "POST", body: JSON.stringify(body) });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = out.detail;
      setNotice(typeof detail === "string" ? detail
        : `${detail?.message || fallback} ${detail?.why_not || detail?.what_closes_it || ""}`);
      return null;
    }
    setNotice(out.message || null);
    loadExceptions();
    return out;
  };

  const openArea = useCallback((key) => {
    apiFetch(`/admin-center/areas/${key}`).then((r) => (r.ok ? r.json() : null)).then(setArea);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <a href="#admin-main" className="sr-only focus:not-sr-only focus:absolute focus:p-2"
         data-testid="admin-skip-link">Skip to main content</a>
      <header className="border-b px-6 py-4 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="admin-title">Administration Center</h1>
          <p className="text-sm text-muted-foreground">
            Configure the platform without code. Every area explains its purpose, dependencies and audit trail.
          </p>
        </div>
        <nav aria-label="Primary" className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm" data-testid="admin-routing-link">
            <Link to="/admin/routing">Routing rules</Link>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="admin-ecc-link">
            <Link to="/command-center">Command Center</Link>
          </Button>
        </nav>
      </header>

      <main id="admin-main" className="max-w-6xl mx-auto p-6 space-y-10">
        {error && <p className="text-sm text-destructive" data-testid="admin-error">{error}</p>}

        {health && (
          <section aria-labelledby="health-heading" data-testid="config-health">
            <h2 id="health-heading" className="text-lg font-medium mb-3">Configuration health</h2>
            <p className="text-sm text-muted-foreground mb-3" data-testid="health-counts">
              {health.errors} error condition(s) · {health.warnings} warning condition(s)
            </p>
            <ul className="space-y-2">
              {health.conditions.filter((c) => c.count > 0).map((c) => (
                <li key={c.code} className="border rounded-md px-4 py-3"
                    data-testid={`health-condition-${c.code}`}>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${
                      c.severity === "error"
                        ? "bg-red-800 text-white"
                        : "bg-amber-100 text-amber-900 border border-amber-300"}`}
                          data-testid={`health-severity-${c.code}`}>
                      {c.severity}
                    </span>
                    <span className="text-sm font-medium">{c.title}</span>
                    <span className="text-sm text-muted-foreground">({c.count})</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{c.remediation}</p>
                </li>
              ))}
              {health.conditions.every((c) => c.count === 0) && (
                <li className="text-sm text-muted-foreground" data-testid="health-clear">
                  No configuration health conditions detected.
                </li>
              )}
            </ul>
          </section>
        )}

        {platform && (
          <section aria-labelledby="platform-heading" data-testid="platform-health">
            <h2 id="platform-heading" className="text-lg font-medium mb-3">Platform health</h2>
            <p className="text-sm text-muted-foreground mb-3" data-testid="platform-rollup">
              Overall: {platform.rollup} · evaluated in {platform.evaluated_in_ms} ms
            </p>
            <div className="grid gap-2 md:grid-cols-4">
              {Object.entries(platform.subsystems).map(([name, s]) => (
                <div key={name} className="border rounded-md px-3 py-2"
                     data-testid={`subsystem-${name}`}>
                  <p className="text-sm font-medium capitalize">{name.replace(/_/g, " ")}</p>
                  <p className={`text-xs font-semibold ${
                    s.status === "healthy" ? "text-emerald-700"
                      : s.status === "failed" ? "text-red-800" : "text-amber-900"}`}>
                    {s.status}
                  </p>
                  {s.detail && <p className="text-xs text-muted-foreground mt-1">{s.detail}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {exceptions && (
          <section aria-labelledby="exceptions-heading" data-testid="acceptance-exceptions"
                   className="space-y-3">
            <h2 id="exceptions-heading" className="text-lg font-medium">Acceptance exceptions</h2>
            <p className="text-sm text-muted-foreground" data-testid="exceptions-method">
              {exceptions.method}
            </p>
            {notice && <p className="text-sm text-amber-900" data-testid="exceptions-notice">{notice}</p>}
            <p className="text-sm" data-testid="exceptions-totals">
              {exceptions.totals.closed} closed · {exceptions.totals.evidence_supplied} with
              evidence · {exceptions.totals.open} open · {exceptions.totals.blocking_open} blocking
              the certificate. Current position: {exceptions.current_certificate.version} —{" "}
              {exceptions.current_certificate.position}.
            </p>
            <ul className="space-y-2">
              {exceptions.rows.map((row) => (
                <li key={row.key} className="border rounded-md px-4 py-3 space-y-2"
                    data-testid={`exception-${row.key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${
                      row.state === "closed" ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                        : row.state === "evidence_supplied"
                          ? "bg-sky-100 text-sky-900 border border-sky-300"
                          : "bg-amber-100 text-amber-900 border border-amber-300"}`}
                          data-testid={`exception-state-${row.key}`}>
                      {row.state.replace("_", " ")}
                    </span>
                    <span className="text-sm font-medium">{row.spec_ref} {row.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {row.owner}{row.blocking ? " · blocks the certificate" : " · carried forward"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground"
                     data-testid={`exception-closes-${row.key}`}>
                    Closed by: {row.what_closes_it}
                  </p>
                  {row.evidence.map((e, i) => (
                    <p key={i} className="text-xs text-muted-foreground"
                       data-testid={`exception-evidence-${row.key}-${i}`}>
                      Evidence from {e.by_email || e.by} on {String(e.at).slice(0, 10)} — {e.note}
                      {e.reference ? ` (${e.reference})` : ""}
                    </p>
                  ))}
                  {row.state === "closed" && (
                    <p className="text-xs text-emerald-900" data-testid={`exception-closed-${row.key}`}>
                      Closed by {row.closed_by_email || "unknown"} — {row.closed_reason}
                    </p>
                  )}
                  {row.state !== "closed" && (
                    <div className="flex flex-wrap gap-2 items-end">
                      <div className="space-y-1 grow min-w-[16rem]">
                        <label htmlFor={`evidence-${row.key}`} className="text-xs font-medium">
                          What is the evidence?
                        </label>
                        <Input id={`evidence-${row.key}`} data-testid={`exception-input-${row.key}`}
                               value={draft[row.key] || ""}
                               onChange={(e) => setDraft({ ...draft, [row.key]: e.target.value })} />
                      </div>
                      <Button size="sm" variant="outline"
                              data-testid={`exception-evidence-save-${row.key}`}
                              disabled={!(draft[row.key] || "").trim()}
                              onClick={() => act(`/acceptance/exceptions/${row.key}/evidence`,
                                                 { note: draft[row.key] },
                                                 "Could not record that evidence")
                                .then((ok) => ok && setDraft({ ...draft, [row.key]: "" }))}>
                        Record evidence
                      </Button>
                      <Button size="sm" data-testid={`exception-close-${row.key}`}
                              disabled={row.evidence.length === 0}
                              onClick={() => act(`/acceptance/exceptions/${row.key}/close`,
                                                 { note: "Evidence accepted by an administrator" },
                                                 "Could not close that exception")}>
                        Close it
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="space-y-2 border-t pt-3">
              <h3 className="text-sm font-semibold">Reissue the certificate</h3>
              <p className="text-xs text-muted-foreground" data-testid="reissue-eligibility">
                {exceptions.may_reissue
                  ? "Every blocking exception is closed — the certificate can be reissued."
                  : exceptions.why_not}
              </p>
              <Button size="sm" data-testid="certificate-reissue"
                      disabled={!exceptions.may_reissue}
                      onClick={() => act("/acceptance/certificate/reissue",
                                         { note: "Reissued from the Administration Center" },
                                         "Could not reissue the certificate")}>
                Reissue it
              </Button>
              {certificate?.issues?.length > 0 && (
                <ul className="text-xs space-y-1" data-testid="certificate-history">
                  {certificate.issues.map((c) => (
                    <li key={c.id} className="text-muted-foreground">
                      {c.version} — {c.position} · issued {String(c.issued_at).slice(0, 10)} by{" "}
                      {c.issued_by_email || c.issued_by} · {c.carried_exceptions.length} carried
                      forward
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {cleanup && (
          <section aria-labelledby="cleanup-heading" data-testid="data-cleanup"
                   className="space-y-3 border rounded-md p-4">
            <h2 id="cleanup-heading" className="text-lg font-medium">Synthetic test data cleanup</h2>
            <p className="text-sm text-muted-foreground" data-testid="cleanup-method">
              {cleanup.method}
            </p>
            <p className="text-sm text-amber-900" data-testid="cleanup-environment">
              {cleanup.environment_note}
            </p>
            <p className="text-sm" data-testid="cleanup-total">
              {cleanup.total} artefact(s) match the test-fixture patterns.{" "}
              {cleanup.published_providers} provider(s) and {cleanup.published_services}{" "}
              catalogue item(s) are published today.
            </p>
            <ul className="space-y-2" data-testid="cleanup-groups">
              {cleanup.groups.filter((g) => g.count > 0).map((g) => (
                <li key={g.group} className="border rounded px-3 py-2"
                    data-testid={`cleanup-group-${g.group}`}>
                  <p className="text-sm font-medium">{g.label} ({g.count})</p>
                  <ul className="mt-1 space-y-0.5">
                    {g.rows.map((row, i) => (
                      <li key={i} className="text-xs text-muted-foreground font-mono"
                          data-testid={`cleanup-row-${g.group}-${i}`}>{row}</li>
                    ))}
                    {g.truncated > 0 && (
                      <li className="text-xs text-muted-foreground">
                        …and {g.truncated} more
                      </li>
                    )}
                  </ul>
                </li>
              ))}
              {cleanup.total === 0 && (
                <li className="text-sm text-muted-foreground" data-testid="cleanup-clear">
                  Nothing to retire — this database holds no matching test artefacts.
                </li>
              )}
            </ul>
            <div className="space-y-2 border-t pt-3">
              <label htmlFor="cleanup-confirm" className="text-sm font-medium block">
                Type {cleanup.confirmation_phrase} to confirm
              </label>
              <Input id="cleanup-confirm" data-testid="cleanup-confirm-input"
                     value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
                     placeholder={cleanup.confirmation_phrase} className="max-w-xs" />
              <Button size="sm" variant="destructive" data-testid="cleanup-run"
                      disabled={cleanupBusy || cleanup.total === 0
                                || confirmation.trim() !== cleanup.confirmation_phrase}
                      onClick={runCleanup}>
                {cleanupBusy ? "Retiring…" : "Retire these artefacts"}
              </Button>
              {cleanupResult && (
                <p className="text-sm text-emerald-900" data-testid="cleanup-result">
                  {cleanupResult}
                </p>
              )}
            </div>
          </section>
        )}

        <section aria-labelledby="areas-heading">
          <h2 id="areas-heading" className="text-lg font-medium mb-3">Administration areas</h2>
          <div className="grid gap-3 md:grid-cols-3" data-testid="admin-areas">
            {areas.map((a) => (
              <Card key={a.key} data-testid={`admin-area-${a.key}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{a.name}</CardTitle>
                  <CardDescription>{a.purpose}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    {a.published_count} published item(s) · {a.health_errors} error(s) ·{" "}
                    {a.health_warnings} warning(s)
                  </p>
                  <Button size="sm" variant="outline" onClick={() => openArea(a.key)}
                          aria-label={`Open ${a.name} administration area`}
                          data-testid={`admin-area-open-${a.key}`}>
                    Open area
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {area && (
          <section aria-labelledby="area-heading" className="space-y-6" data-testid="admin-area-detail">
            <div className="flex items-center justify-between">
              <h2 id="area-heading" className="text-lg font-medium">{area.name}</h2>
              <Button size="sm" variant="ghost" onClick={() => setArea(null)}
                      data-testid="admin-area-close">Close</Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <Block id="purpose" testid="area-purpose">{area.purpose}</Block>
              <Block id="instructions" testid="area-instructions">{area.instructions}</Block>
              <Block id="status" testid="area-status">
                {Object.entries(area.status).map(([k, v]) => `${k}: ${v}`).join(" · ")}
              </Block>
              <Block id="actions" testid="area-actions">{area.actions.join(", ")}</Block>
              <Block id="preview" testid="area-preview">
                Preview available for every type. Test harness:{" "}
                {area.preview_test.test_harness.join(", ") || "none"}
              </Block>
              <Block id="help" testid="area-help">{area.help}</Block>
            </div>

            <div>
              <h3 className="text-base font-medium mb-2">{BLOCK_TITLES.current}</h3>
              <ul className="space-y-1" data-testid="area-current-config">
                {area.current_configuration.map((c) => (
                  <li key={`${c.type}:${c.key}`} className="text-sm border rounded px-3 py-2"
                      data-testid={`area-config-${c.type}-${c.key}`}>
                    <span className="font-medium">{c.name}</span>{" "}
                    <span className="text-muted-foreground">
                      ({c.type} · {c.key} · v{c.config_version})
                    </span>
                    {c.high_impact && (
                      <Badge className="ml-2" variant="outline">second approver required</Badge>
                    )}
                  </li>
                ))}
                {area.current_configuration.length === 0 && (
                  <li className="text-sm text-muted-foreground" data-testid="area-config-empty">
                    Nothing published in this area yet.
                  </li>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-base font-medium mb-2">{BLOCK_TITLES.dependencies}</h3>
              <ul className="space-y-1" data-testid="area-dependencies">
                {area.dependencies.map((d) => (
                  <li key={`${d.type}:${d.key}`} className="text-sm">
                    <span className="font-medium">{d.key}</span> is used by{" "}
                    {d.used_by.map((u) => `${u.type}/${u.key}`).join(", ")}
                  </li>
                ))}
                {area.dependencies.length === 0 && (
                  <li className="text-sm text-muted-foreground">No dependencies recorded.</li>
                )}
              </ul>
            </div>

            <div>
              <h3 className="text-base font-medium mb-2">{BLOCK_TITLES.audit}</h3>
              <ol className="space-y-1" data-testid="area-audit-history">
                {area.audit_history.map((h, i) => (
                  <li key={i} className="text-xs font-mono text-muted-foreground">
                    {new Date(h.at).toLocaleString()} · {h.action} · {h.actor}
                  </li>
                ))}
                {area.audit_history.length === 0 && (
                  <li className="text-sm text-muted-foreground">No audit history yet.</li>
                )}
              </ol>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

const Block = ({ id, testid, children }) => (
  <div className="border rounded-md p-4">
    <h3 className="text-sm font-semibold mb-1">{BLOCK_TITLES[id]}</h3>
    <p className="text-sm text-muted-foreground" data-testid={testid}>{children}</p>
  </div>
);
